import { useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ZoomIn } from "lucide-react";
import { TabletFrame } from "@/components/site/devices";
import { PREVIEW } from "@/constants/testIds";

const pages = [
  { src: "/preview/indice.jpg", badge: "Indice", label: "Tutto il percorso a colpo d'occhio" },
  { src: "/preview/falsa.jpg", badge: "Capitolo 1", label: "La falsa comprensione" },
  { src: "/preview/fasi.jpg", badge: "Le 4 fasi", label: "Le 4 fasi della comprensione" },
  { src: "/preview/esempi.jpg", badge: "Esempi", label: "Esempi guidati per materia" },
  { src: "/preview/attenzione.jpg", badge: "Fase 1", label: "Attenzione — entra con una domanda" },
  { src: "/preview/elaborazione.jpg", badge: "Fase 2", label: "Elaborazione — esercizi pratici" },
  { src: "/preview/collegamento.jpg", badge: "Fase 3", label: "Collegamento — costruisci reti" },
  { src: "/preview/applicazione.jpg", badge: "Capitolo 4", label: "Applicazione reale su un testo" },
];

export default function Preview() {
  const [active, setActive] = useState(null);

  return (
    <section id="anteprima" data-testid={PREVIEW.section} className="relative py-16 sm:py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5982c5]">Guarda dentro</span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1f2937]">
            Sfoglia il prodotto, pagina per pagina
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-[#4b5563]">
            Niente sorprese: queste sono <strong className="text-[#1f2937]">pagine reali</strong> del manuale.
            Capitoli, esempi per materia ed esercizi guidati. Clicca su una pagina per vederla in grande.
          </p>
        </div>

        <div className="mt-12 relative">
          <Carousel opts={{ align: "start", loop: true }} className="w-full">
            <CarouselContent className="-ml-4">
              {pages.map((p, i) => (
                <CarouselItem key={i} className="pl-4 basis-4/5 sm:basis-1/2 lg:basis-1/3">
                  <button
                    data-testid={PREVIEW.item(i)}
                    onClick={() => setActive(p)}
                    className="group relative block w-full text-left"
                  >
                    <div className="relative transition-transform duration-300 group-hover:-translate-y-1">
                      <TabletFrame src={p.src} alt={p.label} />
                      <div className="absolute inset-2 rounded-[0.7rem] bg-gradient-to-t from-[#1f2937]/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4 pointer-events-none">
                        <span className="inline-flex items-center gap-2 bg-white text-[#1f2937] text-sm font-semibold px-3 py-1.5 rounded-full shadow">
                          <ZoomIn size={16} className="text-[#5982c5]" /> Ingrandisci
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-[#5982c5] bg-[#5982c5]/10 px-2.5 py-1 rounded-full">
                        {p.badge}
                      </span>
                      <span className="text-sm font-semibold text-[#1f2937]">{p.label}</span>
                    </div>
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious data-testid={PREVIEW.prev} className="hidden sm:flex -left-4 bg-white border-[#5982c5]/20 text-[#5982c5] hover:bg-[#5982c5] hover:text-white" />
            <CarouselNext data-testid={PREVIEW.next} className="hidden sm:flex -right-4 bg-white border-[#5982c5]/20 text-[#5982c5] hover:bg-[#5982c5] hover:text-white" />
          </Carousel>
          <p className="mt-6 text-center text-sm text-[#9ca3af] sm:hidden">Scorri per vedere altre pagine →</p>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid={PREVIEW.lightbox} className="max-w-3xl bg-transparent border-0 shadow-none p-0">
          {active && (
            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              <DialogTitle className="sr-only">{active.badge} — {active.label}</DialogTitle>
              <DialogDescription className="sr-only">Anteprima ingrandita di una pagina reale del manuale ComprehensionUp.</DialogDescription>
              <div className="flex items-center gap-2 px-5 py-3 bg-[#ecfef7] border-b border-[#5982c5]/10">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#5982c5] bg-[#5982c5]/10 px-2.5 py-1 rounded-full">
                  {active.badge}
                </span>
                <span className="text-sm font-semibold text-[#1f2937]">{active.label}</span>
              </div>
              <div className="max-h-[75vh] overflow-auto bg-[#f8fdfb]">
                <img src={active.src} alt={active.label} className="w-full" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
